import { API_BASE_URL } from "@/lib/config";

export interface CuratedSignal {
  type: "option" | "equity";
  ticker: string;
  action: "BUY" | "SELL";
  strike?: number;
  expiry?: string;
  option_type?: "CALL" | "PUT";
  status: "found" | "rejected";
  reason?: string; // For rejected trades (e.g., "Low liquidity", "Hedge detected")
}

// Full Strategy Detail Types (from GET /strategy/{id})
export interface StrategySignalLeg {
  strike: number;
  expiration: string;
  type: "call" | "put";
  action: "buy" | "sell";
  premium: number;
  delta: number | null;
  implied_volatility: number;
}

export interface StrategySignalMetrics {
  net_cost: number;
  max_profit: number;
  max_loss: number;
  probability_of_profit: number;
  breakeven_prices: number[];
  expiry_date: string;
  expected_profit: number;
  avg_implied_volatility: number;
}

export interface FullStrategySignal {
  type: "options" | "equity";
  ticker: string;
  title: string;
  html_pnl_url?: string;
  html_heatmap_url?: string;
  pnl_png_url?: string;
  heatmap_png_url?: string;
  legs: StrategySignalLeg[];
  metrics: StrategySignalMetrics;
}

export interface FullStrategyNode {
  node_id: string;
  label: string;
  status: "success" | "running" | "idle" | "error" | "pending";
  duration_ms?: number;
  code?: string;
  output_data?: any[];
  stdout?: string;
  description?: string;
  icons?: string[];
}

export interface FullStrategy {
  _id: string;
  strategy_id: string;
  title: string;
  description: string;
  status: "completed" | "running" | "pending" | "error";
  created_at: string;
  last_run_at: string;
  analyst_report_md?: string;
  signals: FullStrategySignal[];
  old_signals?: FullStrategySignal[];
  nodes: FullStrategyNode[];
  notebook_id?: string;
}

export interface CuratedNode {
  id: string;
  label: string;
  description: string;
  status: "active" | "success" | "idle";
}

export interface CuratedStrategy {
  id: string;
  title: string;
  description: string;
  nodes: CuratedNode[];
  connections: [string, string][]; // [source_id, target_id]
  signals: CuratedSignal[];
  isComingSoon?: boolean;
}

// API Response Types (from backend)
interface ApiNode {
  id: string;
  label: string;
  description: string;
  status: "completed" | "running" | "idle" | "error";
}

interface ApiSignal {
  type: "option" | "equity";
  ticker: string;
  action: "BUY" | "SELL";
  strike?: number;
  expiry?: string;
  option_type?: "CALL" | "PUT";
  status: "found";
}

interface ApiPreview {
  id: string;
  title: string;
  description: string;
  nodes: ApiNode[];
  signals: ApiSignal[];
  isComingSoon: boolean;
}

// Synthetic rejected tickers for scanner animation noise
const REJECTED_TICKERS = [
  { ticker: "AAPL", reason: "High IV" },
  { ticker: "TSLA", reason: "Low Volume" },
  { ticker: "MSFT", reason: "Hedge Flow" },
  { ticker: "AMZN", reason: "Strike Delta > 0.3" },
  { ticker: "GOOG", reason: "Binary Event" },
  { ticker: "NFLX", reason: "Low Liquidity" },
  { ticker: "AMD", reason: "IV Percentile > 75%" },
  { ticker: "COIN", reason: "Earnings Risk" },
];

/**
 * Generate synthetic rejected signals to create scanner animation noise.
 * Interleaves real "found" signals with fake "rejected" ones.
 */
function generateScannerSignals(realSignals: ApiSignal[]): CuratedSignal[] {
  const result: CuratedSignal[] = [];
  const usedTickers = new Set(realSignals.map((s) => s.ticker));

  // Filter out rejected tickers that match real signals
  const availableRejected = REJECTED_TICKERS.filter(
    (r) => !usedTickers.has(r.ticker)
  );

  let rejectedIndex = 0;

  for (const signal of realSignals) {
    // Add 1-2 rejected signals before each real one
    const rejectCount = Math.floor(Math.random() * 2) + 1;
    for (
      let i = 0;
      i < rejectCount && rejectedIndex < availableRejected.length;
      i++
    ) {
      const rejected =
        availableRejected[rejectedIndex % availableRejected.length];
      result.push({
        type: "option",
        ticker: rejected.ticker,
        action: "BUY",
        strike: Math.floor(Math.random() * 100) + 100,
        expiry: "12/20",
        option_type: Math.random() > 0.5 ? "CALL" : "PUT",
        status: "rejected",
        reason: rejected.reason,
      });
      rejectedIndex++;
    }

    // Add the real found signal
    result.push({
      type: signal.type,
      ticker: signal.ticker,
      action: signal.action,
      strike: signal.strike,
      expiry: signal.expiry,
      option_type: signal.option_type,
      status: "found",
    });
  }

  return result;
}

/**
 * Map API node status to frontend status.
 */
function mapNodeStatus(apiStatus: ApiNode["status"]): CuratedNode["status"] {
  switch (apiStatus) {
    case "completed":
      return "success";
    case "running":
      return "active";
    default:
      return "idle";
  }
}

/**
 * Transform API preview to frontend CuratedStrategy format.
 */
function transformPreview(preview: ApiPreview): CuratedStrategy {
  const nodes: CuratedNode[] = preview.nodes.map((node) => ({
    id: node.id,
    label: node.label,
    description: node.description,
    status: mapNodeStatus(node.status),
  }));

  // Generate connections from sequential nodes
  const connections: [string, string][] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    connections.push([nodes[i].id, nodes[i + 1].id]);
  }

  return {
    id: preview.id,
    title: preview.title,
    description: preview.description,
    nodes,
    connections,
    signals: generateScannerSignals(preview.signals),
    // Derive isComingSoon from actual data presence, not API flag
    isComingSoon: preview.nodes.length === 0 || preview.signals.length === 0,
  };
}

export const getStrategyById = async (
  id: string,
  isPaid: boolean = true
): Promise<FullStrategy | null> => {
  try {

    const response = await fetch(
      `${API_BASE_URL}/whale/strategy/${id}?is_paid=${isPaid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch strategy: ${response.status}`);
    }

    const data = await response.json();
    return data as FullStrategy;
  } catch (error) {
    return null;
  }
};

export const getCuratedStrategies = async (): Promise<CuratedStrategy[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/whale/previews`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch previews: ${response.status}`);
    }

    const previews: ApiPreview[] = await response.json();

    return previews.map(transformPreview);
  } catch (error) {
    // Return empty array on error - UI will show skeleton/empty state
    return [];
  }
};
